import { memo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { isScheduleValue } from '@/lib/crm/automations/scheduleValue';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { isAnswersSnapshotProperty } from '@/lib/crm/utils/bookingQuestions';
import { formatPhoneNumber, isPhoneNumberValue } from '@/lib/crm/utils/phone';
import { isAllowedUrl } from '@/lib/crm/utils/urlValidation';
import { Dot } from '@/lib/foundations/dot-icon';
import type { PhoneNumber, PropertyValue, QuantityValue } from '@/types/crm';
import { getPropertyValue, unwrapPropertyValue } from '@/types/crm';

import { resolveOptionLabel } from '../booleanOptions';
import { AssociationTypeValueLabel } from './AssociationTypeValueLabel';
import { BookingAnswersInlineLabel } from './BookingAnswersInlineLabel';
import { BookingQuestionsValueLabel } from './BookingQuestionsValueLabel';
import { CurrencyValueLabel } from './CurrencyValueLabel';
import { DateTimeValueLabel } from './DateTimeValueLabel';
import { DateValueLabel } from './DateValueLabel';
import { DurationDaysValueLabel } from './DurationDaysValueLabel';
import { EmailValueLabel } from './EmailValueLabel';
import { EntityTypeValueLabel } from './EntityTypeValueLabel';
import { FileSizeValueLabel } from './FileSizeValueLabel';
import { FormulaValueLabel } from './FormulaValueLabel';
import { LanguageValueLabel } from './LanguageValueLabel';
import { LocationValueLabel } from './LocationValueLabel';
import { ManifestValueLabel } from './ManifestValueLabel';
import { MultiCurrencyValueLabel } from './MultiCurrencyValueLabel';
import { PermissionsValueLabel } from './PermissionsValueLabel';
import { PhoneValueLabel } from './PhoneValueLabel';
import { ScheduleValueLabel } from './ScheduleValueLabel';
import { SelectValueLabel } from './SelectValueLabel';
import { TextValueLabel } from './TextValueLabel';
import { TimezoneValueLabel } from './TimezoneValueLabel';
import { UrlValueLabel } from './UrlValueLabel';
import { WorkingHoursValueLabel } from './WorkingHoursValueLabel';

export interface PropertyValueLabelProps {
  value: unknown;
  /** Organization ID for clickable mention navigation */
  orgId?: number;
  propertyType?: PropertyTypeSchema;
  /** Font size for rendering. 'table' is 13px for DataTable cells. */
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
  /** Apply font-semibold + text-primary styling for visual emphasis */
  bold?: boolean;
  /** Truncate text to this many characters with "…" and show full text on hover */
  maxLength?: number;
  /**
   * When true, truncation still occurs (if `maxLength` is set) but the inline
   * "more/less" button is NOT rendered. Use this when a parent component owns
   * the expand/collapse state (e.g. coordinated old/new diff rendering).
   */
  hideExpandButton?: boolean;
  /**
   * The entity's raw `properties` map (e.g. `object.properties`). Only
   * meaningful when the value is a `formula` field: the formula's target
   * entity type is named by a sibling property (declared via the schema's
   * `field_type_config.target_entity_type_property`), so resolving the
   * formula's schema requires looking that sibling value up here.
   */
  entityProperties?: Record<string, unknown>;
  /**
   * When true, a multi-value (`is_multi`) array value renders EVERY value
   * comma-separated instead of the default first-value + `+N` overflow
   * badge. Default (undefined/false) preserves the `+N` collapse for every
   * other caller. Used by FilterConditionsTooltip, whose vertical wrapping
   * layout has room for the full list and whose `+N` badge is otherwise an
   * unreachable dead end.
   */
  expandAllValues?: boolean;
}

const fontSizeClasses: Record<string, string> = {
  xs: 'text-xs',
  table: 'text-[13px]', // DataTable cell font size
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

const LOCATION_TYPE_LABELS: Record<string, string> = {
  google_meet: 'Google Meet',
  zoom: 'Zoom',
  phone: 'Phone Call',
  in_person: 'In Person',
  custom: 'Custom',
};

/**
 * Build stable, unique React keys for a list of expanded values.
 *
 * `String(v)` collapses every object value to `[object Object]` and lets
 * repeated primitives collide, so React can reuse the wrong stateful child
 * when the list updates. Serializing the value and appending a per-value
 * occurrence discriminator yields a key that is unique even when two values
 * serialize identically, without using the array index (banned as a key).
 */
const buildExpandedValueKeys = (values: unknown[]): string[] => {
  const occurrences = new Map<string, number>();
  return values.map(v => {
    let serialized: string;
    try {
      serialized = JSON.stringify(v) ?? String(v);
    } catch {
      // Circular or otherwise non-serializable value — fall back to String().
      serialized = String(v);
    }
    const occurrence = occurrences.get(serialized) ?? 0;
    occurrences.set(serialized, occurrence + 1);
    return `${serialized}#${occurrence}`;
  });
};

// property value: single-level {value} wrapper — arrays pass through untouched;
// non-arrays are unwrapped one level via unwrapPropertyValue.

/**
 * Renders a single value without recursion.
 * This helper is used for the first item in multi-value arrays.
 *
 * @param value - The value to render
 * @param propertyType - Property schema (with is_multi: false)
 * @param orgId - Organization ID for clickable mentions
 * @param fontSize - Font size for rendering
 * @param className - CSS classes to apply
 * @param truncate - Whether to truncate text
 * @param bold - Whether to apply bold styling
 * @param sizeClass - Pre-computed size class from fontSize
 * @param combinedClassName - Pre-computed combined className with bold
 * @param truncateClasses - Pre-computed truncate classes
 * @param maxLength - Truncate text to this many characters
 * @returns React element rendering the single value
 */
type FontSize = 'xs' | 'table' | 'sm' | 'base' | 'lg';

const renderSingleValue = (
  value: unknown,
  propertyType: PropertyTypeSchema | undefined,
  orgId: number | undefined,
  fontSize: FontSize,
  className: string,
  truncate: boolean | undefined,
  bold: boolean | undefined,
  sizeClass: string,
  combinedClassName: string,
  truncateClasses: string,
  maxLength?: number,
  hideExpandButton?: boolean,
  renderedForMultiValue?: boolean
): React.ReactElement => {
  // Handle null/undefined values
  if (value === null || value === undefined || value === '') {
    return <span className={`${sizeClass} ${combinedClassName}`}>—</span>;
  }

  const unwrappedValue: unknown = unwrapPropertyValue(value);

  // Get field type from propertyType schema
  const fieldType = propertyType?.field_type_name;

  // If value is an array, stringify it (no nested multi-value support).
  // Exception: permissions is inherently a list of grant objects and must be
  // passed through to PermissionsValueLabel intact.
  let displayValue = unwrappedValue;
  if (Array.isArray(unwrappedValue) && fieldType !== 'permissions') {
    displayValue = JSON.stringify(unwrappedValue);
  }

  // A phone property whose value is NOT in canonical shape renders as plain
  // text instead. The CSV import review shows the raw cell from the user's file
  // beside the normalized CRM value, and the phone formatter returns undefined
  // for anything that isn't {country_code, national_number} — painting the
  // value blank, which asks the user to choose between two values while hiding
  // one of them.
  const effectiveFieldType =
    fieldType === 'phone_number' && !isPhoneNumberValue(displayValue)
      ? undefined
      : fieldType;

  // Switch on field type to delegate to specialized components
  switch (effectiveFieldType) {
    case 'phone_number':
      return (
        <PhoneValueLabel
          value={displayValue as PhoneNumber}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'email':
      return (
        <EmailValueLabel
          value={String(displayValue)}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'currency':
      return (
        <CurrencyValueLabel
          value={displayValue as QuantityValue}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'multi_currency':
      return (
        <MultiCurrencyValueLabel
          value={displayValue as QuantityValue[]}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'boolean':
    case 'select':
      return (
        <SelectValueLabel
          value={displayValue as string}
          propertyType={propertyType}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'timezone':
      return (
        <TimezoneValueLabel
          value={displayValue as string}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'language':
      return (
        <LanguageValueLabel
          value={displayValue as string}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'date':
      return (
        <DateValueLabel
          value={displayValue as string}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'datetime':
      return (
        <DateTimeValueLabel
          value={displayValue as string}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'duration_days':
      return (
        <DurationDaysValueLabel
          value={displayValue as string | number}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'file_size':
      return (
        <FileSizeValueLabel
          value={displayValue as string | number}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'url':
      return (
        <UrlValueLabel
          value={displayValue as string}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'markdown': {
      // Multi-value markdown renders inside a <span> wrapper (is_multi=true),
      // so collapse to a plain text preview to avoid <div>-in-<span> DOM nesting.
      // Single-value markdown renders normally via MarkdownContent's <div> wrapper.
      const markdownString = String(displayValue);
      if (renderedForMultiValue) {
        return (
          <span
            className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
            title={truncate ? markdownString : undefined}
          >
            {markdownString}
          </span>
        );
      }
      /* Safe to render as HTML: all markdown values are either (a) ahoy_content, which is
         run through normalize_events in apps/crm_events/services/event_normalizer.py
         (nh3 sanitization with the _SAFE_URL_SCHEMES allowlist) before storage, or (b)
         system-generated (value_write_access: internal/none), sourced from trusted backend code.
         REQUIRED: any new user-editable markdown property MUST be covered by normalize_events
         in apps/crm_events/services/event_normalizer.py before this assumption holds for
         that property.
         The rich text editor (MarkdownInput → PlateNoteEditor) stores these values as
         HTML; MarkdownContent's always-on raw-HTML pipeline (rehypeEscapeUnknownRawTags
         → rehypeRaw → rehypeSanitize) renders it instead of showing literal tags.
         Property rhythm: MarkdownContent's compact paragraphs are 24px lines
         with 8px gaps (sized for inline mention pills in notes); a property
         value sits among 14px/20px rows, so paragraphs here are leading-5
         with mb-1 — the same 20px line as every other row's text. The
         inline editor (MarkdownInput 'inline') types at the same metrics so
         click-to-edit swaps in place. [&_p:empty]:min-h-5 — a blank line the
         author typed serializes as an empty <p>, which otherwise renders 0px
         tall here while the editor necessarily gives it a caret line;
         keeping it one line tall preserves the author's blank line AND
         keeps read/edit lines aligned. Scoped here (not in MarkdownContent)
         so chat/notes surfaces are untouched. */
      return (
        <MarkdownContent
          content={markdownString}
          className={`${sizeClass} [&_p]:leading-5 [&_p]:mb-1 [&_p:empty]:min-h-5 ${combinedClassName}${truncate ? ' line-clamp-1 overflow-hidden' : ''}`}
          size='compact'
          orgId={orgId}
        />
      );
    }

    case 'manifest':
      return (
        <ManifestValueLabel
          value={displayValue}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'ahoy_report_query':
      // Read-only display of the saved query. Lists/record views show this
      // as JSON; the chart visualisation happens via the manifest envelope
      // returned by execute_saved_report (see InlineReportChart).
      return (
        <pre
          className={`${sizeClass} font-mono ${truncate ? truncateClasses : 'whitespace-pre-wrap break-words'} ${combinedClassName}`}
        >
          {displayValue && typeof displayValue === 'object'
            ? JSON.stringify(displayValue, null, 2)
            : String(displayValue ?? '—')}
        </pre>
      );

    case 'working_hours_schedule':
      return (
        <WorkingHoursValueLabel
          value={displayValue}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={renderedForMultiValue ? true : truncate}
        />
      );

    case 'booking_questions':
      return (
        <BookingQuestionsValueLabel
          value={displayValue}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={renderedForMultiValue ? true : truncate}
        />
      );

    case 'permissions':
      return (
        <PermissionsValueLabel
          value={displayValue}
          orgId={orgId}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={renderedForMultiValue ? true : truncate}
        />
      );

    case 'location_type': {
      const label =
        LOCATION_TYPE_LABELS[String(displayValue)] ?? String(displayValue);
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
        >
          {label}
        </span>
      );
    }

    case 'color': {
      const colorStr = String(displayValue);
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${combinedClassName} inline-flex items-center gap-1.5`}
        >
          <Dot size='sm' style={{ color: colorStr }} />
          {colorStr}
        </span>
      );
    }

    case 'permission_group':
      // Permission groups are system-managed; render as plain text
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
        >
          {String(displayValue)}
        </span>
      );

    case 'association_type':
      return (
        <AssociationTypeValueLabel
          value={String(displayValue)}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'entity_type':
      return (
        <EntityTypeValueLabel
          value={String(displayValue)}
          orgId={orgId}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
        />
      );

    case 'domain': {
      // Prepend https:// if value doesn't start with http
      const domainStr = String(displayValue);
      const domainUrl = domainStr.startsWith('http')
        ? domainStr
        : `https://${domainStr}`;
      if (!isAllowedUrl(domainUrl)) {
        return (
          <span className={`${sizeClass} ${combinedClassName}`}>
            {domainStr}
          </span>
        );
      }
      return (
        <a
          href={encodeURI(domainUrl)}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={`${domainStr} (opens in new tab)`}
          className={`${sizeClass} ${combinedClassName} underline decoration-solid transition-colors`}
        >
          {domainStr}
        </a>
      );
    }

    case 'number': {
      // Strip trailing decimal zeros: "5.00000000" → "5", "3.50" → "3.5"
      // Use string manipulation instead of Number() to avoid precision loss on large integers
      // Skip stripping for scientific notation (e.g. 1.5e-10) to avoid corrupting the exponent
      const stringValue = String(displayValue);
      const numFormatted =
        stringValue.includes('.') && !/[eE]/.test(stringValue)
          ? stringValue.replace(/\.?0+$/, '')
          : stringValue;

      // Apply maxLength truncation if specified
      const truncatedNum =
        maxLength && numFormatted.length > maxLength
          ? numFormatted.slice(0, maxLength) + '…'
          : numFormatted;

      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
          title={truncate ? numFormatted : undefined}
        >
          {truncatedNum}
        </span>
      );
    }

    // A scheduled automation's recurrence ({rrule, dtstart, tz}) — the same
    // wording the record's Schedule section uses, flattened to one line for
    // cells and detail rows. Anything without an rrule falls back to the
    // generic text rendering below.
    case 'automation_schedule': {
      if (isScheduleValue(displayValue)) {
        return (
          <ScheduleValueLabel
            value={displayValue}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );
      }
      return (
        <TextValueLabel
          value={
            typeof displayValue === 'object'
              ? JSON.stringify(displayValue)
              : String(displayValue)
          }
          orgId={orgId}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
          maxLength={maxLength}
          hideExpandButton={hideExpandButton}
        />
      );
    }

    // Simple string types: text, etc.
    default: {
      // Handle legacy pipeline_stage format: { pipeline_name, stage_name }
      if (
        typeof displayValue === 'object' &&
        displayValue !== null &&
        'pipeline_name' in displayValue &&
        'stage_name' in displayValue
      ) {
        const stageName = (displayValue as { stage_name: string }).stage_name;
        const formatted = stageName.replace(/^ahoy_/, '').replace(/_/g, ' ');
        const stageDisplayValue =
          formatted.charAt(0).toUpperCase() + formatted.slice(1);
        return (
          <span
            className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
            title={truncate ? stageDisplayValue : undefined}
          >
            {stageDisplayValue}
          </span>
        );
      }

      // Convert to string for display - use TextValueLabel to handle @mentions
      let stringValue =
        typeof displayValue === 'object'
          ? JSON.stringify(displayValue)
          : String(displayValue);

      // Format known internal property values for display
      if (propertyType?.property_type_name === 'ahoy_notes_source') {
        const sourceLabels: Record<string, string> = {
          google_meet: 'Google Meet',
          granola: 'Granola',
          fathom: 'Fathom',
        };
        stringValue = sourceLabels[stringValue] ?? stringValue;
      }

      if (propertyType?.property_type_name === 'ahoy_location') {
        return (
          <LocationValueLabel
            value={stringValue}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );
      }

      return (
        <TextValueLabel
          value={stringValue}
          orgId={orgId}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
          maxLength={maxLength}
          hideExpandButton={hideExpandButton}
        />
      );
    }
  }
};

/**
 * Universal display component for CRM property values.
 * Automatically switches to the appropriate specialized label based on field_type_name.
 *
 * Supported field types:
 * - phone_number: PhoneValueLabel with click-to-call
 * - currency: CurrencyValueLabel for currency amounts
 * - multi_currency: MultiCurrencyValueLabel for arrays of currency amounts
 * - select: SelectValueLabel with option label lookup
 * - date: DateValueLabel with locale formatting
 * - datetime: DateTimeValueLabel with locale formatting
 * - file_size: FileSizeValueLabel for human-readable byte sizes
 * - url: UrlValueLabel for clickable external links
 * - domain: Clickable https link for domain names
 * - markdown: MarkdownContent with entity tag rendering
 * - manifest: ManifestValueLabel for dynamic view components
 * - permissions: PermissionsValueLabel with entity-type label badges
 * - text, number, email: Simple string display (plain text)
 *
 * @example
 * <PropertyValueLabel
 *   value={getPropertyValue(contact.properties?.ahoy_phone_number)}
 *   propertyType={schema['ahoy_phone_number']}
 * />
 */
export const PropertyValueLabel: React.FC<PropertyValueLabelProps> = memo(
  ({
    value,
    orgId,
    propertyType,
    fontSize = 'sm',
    className = '',
    truncate,
    bold,
    maxLength,
    hideExpandButton,
    entityProperties,
    expandAllValues,
  }) => {
    const sizeClass = fontSizeClasses[fontSize];
    const truncateClasses = truncate
      ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
      : '';

    // Combine className with bold styling (font-semibold + text-primary for emphasis)
    const combinedClassName =
      `${className} ${bold ? 'font-semibold text-primary' : ''}`.trim();

    // Formula fields resolve their own schema (FormulaValueLabel composes it
    // via useFormulaTargetSchema) rather than relying on a caller-supplied
    // schema — every caller only needs to pass the entity's raw `properties`
    // map so the formula's target entity type (named by the sibling property
    // in `field_type_config.target_entity_type_property`) can be looked up.
    // This intercept must come before the null/empty-value early return below
    // so FormulaDisplay's own "—" placeholder rendering stays in one place.
    if (propertyType?.field_type_name === 'formula') {
      const targetProp =
        propertyType.field_type_config?.target_entity_type_property;
      const targetEntityTypeName = targetProp
        ? getPropertyValue<string>(
            entityProperties?.[targetProp] as PropertyValue | string | undefined
          )
        : undefined;
      // Unwrap the supported `{ value: ... }` CRM property-value shape before
      // handing the formula to FormulaValueLabel — otherwise a wrapped value
      // renders as JSON instead of formula text. getPropertyValue passes raw
      // (already-unwrapped) values through unchanged.
      const formulaValue = getPropertyValue<unknown>(
        value as PropertyValue | string | undefined
      );
      return (
        <FormulaValueLabel
          formula={formulaValue}
          targetEntityTypeName={targetEntityTypeName}
          orgId={orgId}
          truncate={truncate}
          maxLength={maxLength}
          className={`${sizeClass} ${combinedClassName}`}
        />
      );
    }

    // Handle null/undefined values
    if (value === null || value === undefined || value === '') {
      return <span className={`${sizeClass} ${combinedClassName}`}>—</span>;
    }

    const unwrappedValue: unknown = unwrapPropertyValue(value);

    // Answers-snapshot json properties (ahoy_custom_answers / ahoy_booking_answers)
    // are native arrays of {key,label,type,answer}. Intercept BEFORE the multi-value
    // branch and the non-multi array→JSON.stringify at ~line 878 so every surface
    // that hard-imports PropertyValueLabel renders readable Q&A text, not raw JSON.
    if (isAnswersSnapshotProperty(propertyType?.property_type_name)) {
      return (
        <BookingAnswersInlineLabel
          value={unwrappedValue}
          fontSize={fontSize}
          className={combinedClassName}
          truncate={truncate}
          maxLength={maxLength}
        />
      );
    }

    // Handle multi-value properties (is_multi: true with array values)
    if (propertyType?.is_multi && Array.isArray(unwrappedValue)) {
      // Filter out null/undefined/empty string values
      const validValues = unwrappedValue.filter(
        v => v !== null && v !== undefined && v !== ''
      );

      // If no valid values, render placeholder
      if (validValues.length === 0) {
        return <span className={`${sizeClass} ${combinedClassName}`}>—</span>;
      }

      // For composite properties (with subproperties), extract display_name from each element
      // NOTE: Check .length because the API returns [] (empty array, truthy) for non-composite types
      const displayValues = propertyType?.subproperties?.length
        ? validValues.map(v => {
            if (typeof v === 'object' && v !== null) {
              const displayName = (v as { display_name?: unknown })
                .display_name;
              if (displayName !== null && displayName !== undefined) {
                return displayName;
              }
              const fallbackKey = Object.keys(v).find(
                key => key !== 'display_name'
              );
              return fallbackKey
                ? (v as Record<string, unknown>)[fallbackKey]
                : displayName;
            }
            return v;
          })
        : validValues;

      // Expanded mode (e.g. filter tooltip): render every value
      // comma-separated with no `+N` collapse. Iterates the post-filter
      // `displayValues` so the empty-array `—` guard, null filtering, and
      // composite display_name extraction above all still apply.
      if (expandAllValues) {
        const expandedValueKeys = buildExpandedValueKeys(displayValues);
        return (
          <span
            className={`${sizeClass} ${combinedClassName} inline-flex flex-wrap items-center gap-x-1 gap-y-0.5 min-w-0 max-w-full`}
          >
            {displayValues.map((v, index) => (
              <span
                key={expandedValueKeys[index]}
                className='inline-flex items-center'
              >
                {index > 0 && <span className='mr-1 opacity-40'>,</span>}
                {renderSingleValue(
                  v,
                  { ...propertyType, is_multi: false },
                  orgId,
                  fontSize,
                  className,
                  truncate,
                  bold,
                  sizeClass,
                  combinedClassName,
                  truncateClasses,
                  maxLength,
                  hideExpandButton,
                  true
                )}
              </span>
            ))}
          </span>
        );
      }

      // Show first value + count of remaining
      const remainingCount = displayValues.length - 1;

      // Format all values as comma-separated string for tooltip
      const formatValueForTooltip = (v: unknown): string => {
        if (v === null || v === undefined) {
          return '';
        }
        // Handle phone numbers specially
        if (
          typeof v === 'object' &&
          v !== null &&
          'country_code' in v &&
          'national_number' in v
        ) {
          return formatPhoneNumber(v as PhoneNumber);
        }
        // Select/boolean values are stored as option keys — resolve to the
        // option's label the same way SelectValueLabel does, so the "+N"
        // tooltip reads "Arena Club, Beezie" instead of "arena_club, beezie".
        if (
          (propertyType?.field_type_name === 'select' ||
            propertyType?.field_type_name === 'boolean') &&
          typeof v !== 'object'
        ) {
          return resolveOptionLabel(propertyType, String(v));
        }
        if (typeof v === 'object') {
          return JSON.stringify(v);
        }
        return String(v);
      };
      const allValuesText = displayValues.map(formatValueForTooltip).join(', ');

      return (
        // Multi-value wrapper: an inline-flex row of `[first value] [+N]`.
        //
        // ⚠️ CSS gotcha — `text-overflow: ellipsis` is **ignored on
        // inline-flex containers** (per spec it only applies to block
        // container boxes). We previously set ellipsis classes here and
        // they silently did nothing — narrow contexts hard-clipped emails
        // and URLs without showing the "…" marker. The fix is structural:
        //
        //   1. The inline-flex wrapper gets `min-w-0 max-w-full` so it
        //      can shrink to fit a constrained parent (e.g. the narrow
        //      slideout / left column of the full-page view) instead of
        //      sizing to its content and overflowing.
        //   2. The first value is wrapped in a `flex-1 min-w-0` span so
        //      it acts as a flex item that fills the available width.
        //      The block-level renderer inside (TextValueLabel etc., when
        //      called with truncate=true) then has a definite width to
        //      ellipsize against — text-overflow finally has somewhere to
        //      place the "…".
        //   3. The `+N` badge gets `shrink-0` so the available-width math
        //      reserves space for it instead of squashing it.
        //
        // This contract is repeatable for ANY new multi-value property
        // type — the field-type-specific renderer doesn't need to know
        // about truncation plumbing, only its own `truncate` prop.
        <span
          className={`${sizeClass} ${combinedClassName} inline-flex items-center gap-1 min-w-0 max-w-full`}
          title={truncate ? allValuesText : undefined}
        >
          {/* `flex items-center` (not a bare block): renderers whose value
              sits in an inline-block hover pill with vertical padding and
              negative margins (PhoneValueLabel's copy affordance) otherwise
              grow the line box and paint the text ~4px above every other
              cell's baseline. Centering the inner box keeps all field types
              on one visual baseline; block children keep their ellipsis via
              min-w-0 + flex-1. */}
          <span className='flex-1 min-w-0 flex items-center'>
            {renderSingleValue(
              displayValues[0],
              { ...propertyType, is_multi: false },
              orgId,
              fontSize,
              className,
              truncate,
              bold,
              sizeClass,
              combinedClassName,
              truncateClasses,
              maxLength,
              hideExpandButton,
              true
            )}
          </span>
          {remainingCount > 0 && (
            <span className='shrink-0' title={allValuesText}>
              <Badge color='gray' size='sm'>
                +{remainingCount}
              </Badge>
            </span>
          )}
        </span>
      );
    }

    // Get field type from propertyType schema
    const fieldType = propertyType?.field_type_name;

    // Backwards compatibility: if value is an array but is_multi is false,
    // fall through to default rendering (will be stringified as JSON)
    // This handles legacy data where arrays were stored without is_multi flag.
    // Exceptions: multi_currency is inherently an array of {amount, unit} (backend
    // value_type = multi_quantity) and must be passed through to MultiCurrencyValueLabel;
    // permissions is inherently a list of grant objects for PermissionsValueLabel.
    let displayValue = unwrappedValue;
    if (
      Array.isArray(unwrappedValue) &&
      !propertyType?.is_multi &&
      fieldType !== 'multi_currency' &&
      fieldType !== 'permissions'
    ) {
      displayValue = JSON.stringify(unwrappedValue);
    }

    // Same canonical-shape guard as renderSingleValue() above — see the comment
    // there for why a non-canonical phone value must not reach PhoneValueLabel.
    const effectiveFieldType =
      fieldType === 'phone_number' && !isPhoneNumberValue(displayValue)
        ? undefined
        : fieldType;

    // Switch on field type to delegate to specialized components
    switch (effectiveFieldType) {
      case 'phone_number':
        return (
          <PhoneValueLabel
            value={displayValue as PhoneNumber}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'email':
        // Kept in sync with renderSingleValue() above (multi-value path).
        return (
          <EmailValueLabel
            value={String(displayValue)}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'currency':
        return (
          <CurrencyValueLabel
            value={displayValue as QuantityValue}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'multi_currency': {
        // Raw wire shape is a bare list of `{ value: { amount, unit }, ... }`
        // wrappers. getPropertyValue's array branch extracts `item.value` per
        // element; already-unwrapped `[{ amount, unit }]` elements have no
        // `value` key and pass through unchanged — so the array branch
        // converges both wire forms to QuantityValue[]. Matches the canonical
        // util in types/crm.ts. A non-array displayValue is a caller/schema
        // violation (multi_currency is always backed by an array) and falls
        // through to undefined, which MultiCurrencyValueLabel's existing
        // guard renders as "—".
        const currencyValues = Array.isArray(displayValue)
          ? getPropertyValue<QuantityValue[]>(displayValue)
          : undefined;
        return (
          <MultiCurrencyValueLabel
            value={currencyValues}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );
      }

      case 'boolean':
      case 'select':
        return (
          <SelectValueLabel
            value={displayValue as string}
            propertyType={propertyType}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'timezone':
        return (
          <TimezoneValueLabel
            value={displayValue as string}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'language':
        return (
          <LanguageValueLabel
            value={displayValue as string}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'date':
        return (
          <DateValueLabel
            value={displayValue as string}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'datetime':
        return (
          <DateTimeValueLabel
            value={displayValue as string}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'duration_days':
        return (
          <DurationDaysValueLabel
            value={displayValue as string | number}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'file_size':
        return (
          <FileSizeValueLabel
            value={displayValue as string | number}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'url':
        return (
          <UrlValueLabel
            value={displayValue as string}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'markdown':
        /* The rich text editor (MarkdownInput → PlateNoteEditor) stores
           markdown property values as sanitized HTML; MarkdownContent's
           always-on raw-HTML pipeline renders it instead of showing literal
           tags. Kept in sync with renderSingleValue() above — including the
           leading-5 / mb-1 property rhythm and [&_p:empty]:min-h-5, which
           keeps an author-typed blank line one line tall so the inline
           editor's caret line for it matches. */
        return (
          <MarkdownContent
            content={String(displayValue)}
            className={`${sizeClass} [&_p]:leading-5 [&_p]:mb-1 [&_p:empty]:min-h-5 ${combinedClassName}${truncate ? ' line-clamp-1 overflow-hidden' : ''}`}
            size='compact'
            orgId={orgId}
          />
        );

      case 'manifest':
        return (
          <ManifestValueLabel
            value={displayValue}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'ahoy_report_query':
        // Read-only display of the saved query as formatted JSON. Kept in
        // sync with renderSingleValue() above so single-value rendering of
        // ahoy_report_query (record views, proposal previews) matches the
        // multi-value path.
        return (
          <pre
            className={`${sizeClass} font-mono ${truncate ? truncateClasses : 'whitespace-pre-wrap break-words'} ${combinedClassName}`}
          >
            {displayValue && typeof displayValue === 'object'
              ? JSON.stringify(displayValue, null, 2)
              : String(displayValue ?? '—')}
          </pre>
        );

      case 'working_hours_schedule':
        return (
          <WorkingHoursValueLabel
            value={displayValue}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'booking_questions':
        return (
          <BookingQuestionsValueLabel
            value={displayValue}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'permissions':
        return (
          <PermissionsValueLabel
            value={displayValue}
            orgId={orgId}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'location_type': {
        const label =
          LOCATION_TYPE_LABELS[String(displayValue)] ?? String(displayValue);
        return (
          <span
            className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
          >
            {label}
          </span>
        );
      }

      case 'color': {
        const colorStr = String(displayValue);
        return (
          <span
            className={`${sizeClass} ${truncateClasses} ${combinedClassName} inline-flex items-center gap-1.5`}
          >
            <Dot size='sm' style={{ color: colorStr }} />
            {colorStr}
          </span>
        );
      }

      case 'permission_group':
        // Permission groups are system-managed; render as plain text
        return (
          <span
            className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
          >
            {String(displayValue)}
          </span>
        );

      case 'association_type':
        return (
          <AssociationTypeValueLabel
            value={String(displayValue)}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'entity_type':
        return (
          <EntityTypeValueLabel
            value={String(displayValue)}
            orgId={orgId}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
          />
        );

      case 'domain': {
        // Prepend https:// if value doesn't start with http
        const domainStr = String(displayValue);
        const domainUrl = domainStr.startsWith('http')
          ? domainStr
          : `https://${domainStr}`;
        if (!isAllowedUrl(domainUrl)) {
          return (
            <span className={`${sizeClass} ${combinedClassName}`}>
              {domainStr}
            </span>
          );
        }
        return (
          <a
            href={encodeURI(domainUrl)}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`${domainStr} (opens in new tab)`}
            className={`${sizeClass} ${combinedClassName} underline decoration-solid transition-colors`}
          >
            {domainStr}
          </a>
        );
      }

      case 'number': {
        // Strip trailing decimal zeros: "5.00000000" → "5", "3.50" → "3.5"
        // Use string manipulation instead of Number() to avoid precision loss on large integers
        // Skip stripping for scientific notation (e.g. 1.5e-10) to avoid corrupting the exponent
        const stringValue = String(displayValue);
        const numFormatted2 =
          stringValue.includes('.') && !/[eE]/.test(stringValue)
            ? stringValue.replace(/\.?0+$/, '')
            : stringValue;

        // Apply maxLength truncation if specified
        const truncatedValue =
          maxLength && numFormatted2.length > maxLength
            ? numFormatted2.slice(0, maxLength) + '…'
            : numFormatted2;

        return (
          <span
            className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
            title={
              truncate || truncatedValue !== numFormatted2
                ? numFormatted2
                : undefined
            }
          >
            {truncatedValue}
          </span>
        );
      }

      // A scheduled automation's recurrence — see renderSingleValue for the
      // same case; a value with no rrule falls through to the text path.
      case 'automation_schedule': {
        if (isScheduleValue(displayValue)) {
          return (
            <ScheduleValueLabel
              value={displayValue}
              fontSize={fontSize}
              className={combinedClassName}
              truncate={truncate}
            />
          );
        }
        return (
          <TextValueLabel
            value={
              typeof displayValue === 'object'
                ? JSON.stringify(displayValue)
                : String(displayValue)
            }
            orgId={orgId}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
            maxLength={maxLength}
            hideExpandButton={hideExpandButton}
          />
        );
      }

      // Simple string types: text, etc.
      default: {
        // Handle legacy pipeline_stage format: { pipeline_name, stage_name }
        if (
          typeof displayValue === 'object' &&
          displayValue !== null &&
          'pipeline_name' in displayValue &&
          'stage_name' in displayValue
        ) {
          const stageName = (displayValue as { stage_name: string }).stage_name;
          const formatted = stageName.replace(/^ahoy_/, '').replace(/_/g, ' ');
          const stageDisplayValue =
            formatted.charAt(0).toUpperCase() + formatted.slice(1);
          return (
            <span
              className={`${sizeClass} ${truncateClasses} ${combinedClassName}`}
              title={truncate ? stageDisplayValue : undefined}
            >
              {stageDisplayValue}
            </span>
          );
        }

        // Convert to string for display - use TextValueLabel to handle @mentions
        const stringValue =
          typeof displayValue === 'object'
            ? JSON.stringify(displayValue)
            : String(displayValue);

        if (propertyType?.property_type_name === 'ahoy_location') {
          return (
            <LocationValueLabel
              value={stringValue}
              fontSize={fontSize}
              className={combinedClassName}
              truncate={truncate}
            />
          );
        }

        return (
          <TextValueLabel
            value={stringValue}
            orgId={orgId}
            fontSize={fontSize}
            className={combinedClassName}
            truncate={truncate}
            maxLength={maxLength}
            hideExpandButton={hideExpandButton}
          />
        );
      }
    }
  }
);

PropertyValueLabel.displayName = 'PropertyValueLabel';
